<template>
  <div>
    <h3 class="subtitle-1 font-weight-bold mb-3">
      {{ $t('admin.productsDatabase.importWizard.steps.coverArt.title') }}
    </h3>
    
    <p class="mb-4">
      {{ $t('admin.productsDatabase.importWizard.steps.coverArt.description') }}
    </p>

    <v-row>
      <v-col
        cols="12"
        md="8"
      >
        <FileUpload
          :upload-url="uploadUrl"
          accept-types="image/*"
          file-types="image files"
          :max-file-size="maxFileSize"
          :validate-file="validateImageFile"
          :include-auth-token="true"
          :show-upload-button="false"
          :drag-drop-prompt="$t('admin.productsDatabase.importWizard.steps.coverArt.dragDrop')"
          :uploading-message="$t('admin.productsDatabase.importWizard.steps.coverArt.uploading')"
          :success-message="$t('admin.productsDatabase.importWizard.steps.coverArt.success')"
          :browse-files-text="$t('admin.productsDatabase.importWizard.steps.coverArt.browseFiles')"
          :upload-button-text="$t('admin.productsDatabase.importWizard.steps.coverArt.uploadCoverArt')"
          @file-selected="handleFileSelected"
          @upload-success="handleUploadSuccess"
          @upload-error="handleUploadError"
          @upload-progress="handleUploadProgress"
          @reset="resetUpload"
        />
      </v-col>
      <v-col
        cols="12"
        md="4"
        class="d-flex justify-center align-center"
      >
        <div class="cover-preview-container">
          <book-cover-image
            v-if="coverArtUrl"
            :src="coverArtUrl"
            :alt="$t('bookInfo.bookCoverAlt', { title: $t('bookInfo.title') })"
            max-height="250"
            contain
            class="cover-preview"
          />
          <div
            v-else
            class="cover-placeholder d-flex justify-center align-center"
          >
            <v-icon
              size="48"
              color="secondary"
            >
              mdi-image-outline
            </v-icon>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Status information -->
    <v-alert
      v-if="isUploaded && mediaObject" 
      color="success"
      outlined
      dense
      class="mt-4"
    >
      {{ $t('admin.productsDatabase.importWizard.steps.coverArt.coverArtUploadedSuccess') }}
    </v-alert>
  </div>
</template>

<script>
import BookCoverImage from "@/components/BookCoverImage";
import FileUpload from '@/components/FileUpload.vue';
import { removeIriId } from '@/iris';
import { FILE_SIZE_LIMITS } from '@/utils/Const';

// Get API base URL from the environment or use a default
const apiBaseUrl = process.env.VUE_APP_API_HOST || 'https://localhost';

export default {
  name: 'CoverArtUpload',
  components: {
    BookCoverImage,
    FileUpload
  },
  props: {
    bookId: {
      type: String,
      required: true
    },
    initialCoverUrl: {
      type: String,
      default: null
    },
    productInfoManager: {
      type: Object,
      default: null
    }
  },
  emits: ['cancel', 'next', 'cover-uploaded', 'upload-error', 'valid'],
  data() {
    return {
      maxFileSize: FILE_SIZE_LIMITS.COVER_ART_UPLOAD,
      isUploaded: false,
      hasError: false,
      selectedFile: null,
      uploadProgress: 0,
      coverArtUrl: this.initialCoverUrl,
      mediaObject: null
    };
  },
  computed: {
    uploadUrl() {
      let sanitizedBookId = removeIriId(this.bookId);
      return `${apiBaseUrl}/books/${sanitizedBookId}/cover`;
    },
    canProceed() {
      // Can proceed if either we have a cover image already uploaded
      // or we've just uploaded a new one successfully
      return this.coverArtUrl !== null && !this.hasError;
    },
    isValid() {
      return this.canProceed;
    }
  },
  watch: {
    isValid(newVal) {
      this.$emit('valid', newVal);
    }
  },
  mounted() {
    // Emit initial validation state
    this.$emit('valid', this.isValid);
  },
  methods: {
    validateImageFile(file) {
      // Check if file is an image
      const validImageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!validImageTypes.includes(file.type)) {
        return this.$t('admin.productsDatabase.importWizard.steps.coverArt.errors.invalidImageFormat');
      }
      return true;
    },
    
    handleFileSelected(file) {
      this.selectedFile = file;
      this.isUploaded = false;
      this.hasError = false;
    },
    
    handleUploadProgress(progress) {
      this.uploadProgress = progress;
    },
    
    handleUploadSuccess(data) {
      this.isUploaded = true;
      
      this.mediaObject = data.mediaObject;
      
      this.coverArtUrl = data.mediaObject.filePath;
      
      if (this.productInfoManager) {
        this.productInfoManager.updateFieldAlreadySaved('book.imageUrl', this.coverArtUrl);
        this.productInfoManager.updateFieldAlreadySaved('book.thumbnailUrl', this.coverArtUrl);
      }
      
      this.$store.dispatch('snackbar/showSnackbar', {
        text: this.$t('admin.productsDatabase.importWizard.steps.coverArt.coverArtUploadedSuccess'),
        color: 'success'
      });
    },
    
    handleUploadError(errorInfo) {
      this.hasError = true;
      console.error('Cover art upload error:', errorInfo.error);
      
      this.$emit('upload-error', errorInfo);
      
      this.$store.dispatch('snackbar/showSnackbar', {
        text: errorInfo.message || this.$t('admin.productsDatabase.importWizard.steps.coverArt.coverArtUploadFailed'),
        color: 'error'
      });
    },
    
    resetUpload() {
      this.isUploaded = false;
      this.hasError = false;
      this.selectedFile = null;
      this.uploadProgress = 0;
      // We don't reset coverArtUrl here to preserve any existing image
    }
  }
};
</script>

<style scoped>
.cover-preview-container {
  width: 100%;
  border: 1px solid var(--v-lightAccent-base);
  border-radius: 4px;
  padding: 8px;
  height: 250px;
}

.cover-placeholder {
  height: 100%;
  background-color: var(--v-lightestAccent-base);
  border-radius: 4px;
}

.cover-preview {
  border-radius: 4px;
}
</style>
